import { ORM, Model as ORMModel, ForeignKey, createSelector as ormSelector } from "redux-orm";
import { AUTH_LOGOUT } from "../auth/actions";

const orm = new ORM();
var timer;
export default orm;

export function createSelector(...args) {
    return ormSelector(orm, state => state.orm, ...args);
}

export function selectAll(cls, serialize = obj => obj.ref) {
    return createSelector(schema =>
        schema[cls.modelName]
            .all()
            .toModelArray()
            .map(serialize)
    );
}

export function selectByUrlId(cls, serialize = obj => obj.ref, param = "id") {
    return createSelector(
        (state, ownProps) => ownProps.match.params[param],
        (schema, objId) => {
            cls = schema[cls.modelName];
            if (cls.hasId(objId)) {
                var obj = cls.withId(objId);
                return serialize(obj);
            } else {
                return null;
            }
        }
    );
}

function isRealUser(authState) {
    return authState && authState.user;
}

export class BaseModel extends ORMModel {
    static selectAll(serializer) {
        return selectAll(this, serializer);
    }

    static selectByUrlId(serializer, param = "id") {
        return selectByUrlId(this, serializer, param);
    }

    static get source() {
        return null;
    }

    static get isUserData() {
        return null;
    }

    static get isSettings() {
        return null;
    }

    static get actions() {
        const functionPrefix = `orm${this.modelName}`,
            typePrefix = `ORM_${this.modelName.toUpperCase()}`;
        return {
            [`${functionPrefix}Reload`]: () => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState();
                    if (cls.isUserData && !cls.isSettings) {
                        if (!isRealUser(state.auth)) {
                            return;
                        }
                    }
                    dispatch({
                        type: `${typePrefix}_PULLING`
                    });
                    let options = {};
                    if (!cls.isSettings) {
                        options.headers = {
                            Authorization: "Token " + state.auth.user.auth_token
                        };
                    }
                    fetch(`${cls.source}?format=json&t=${Date.now()}`, options)
                        .then(result => result.json())
                        .then(data => {
                            if (data.list) {
                                data = data.list;
                            }
                            if (!(data instanceof Array)) {
                                throw new Error(data.detail || data);
                            }
                            return data;
                        })
                        .then(data =>
                            dispatch({
                                type: `${typePrefix}_PULLED`,
                                payload: data
                            })
                        )
                        .catch(e => {
                            if (e.message === "Invalid token.") {
                                dispatch({
                                    type: AUTH_LOGOUT
                                });
                            }
                            dispatch({
                                type: `${typePrefix}_PULLERROR`,
                                error: e
                            });
                        });
                };
            },
            [`${functionPrefix}ReloadDynamic`]: attributes => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState();
                    dispatch({
                        type: `${typePrefix}_PULLING`
                    });
                    let options = {};
                    options.headers = {
                        Authorization: "Token " + state.auth.user.auth_token
                    };
                    return fetch(`${cls.source}?format=json&t=${Date.now()}${attributes}`, options)
                        .then(result => result.json())
                        .then(data => {
                            if (data.list) {
                                data = data.list;
                            }
                            if (!(data instanceof Array)) {
                                throw new Error(data.detail || data);
                            }
                            return data;
                        })
                        .then(data => {
                            dispatch({
                                type: `${typePrefix}_PULLED_DELETEDYNAMIC`,
                                payload: data
                            });
                        })
                        .catch(e => {
                            if (e.message === "Invalid token.") {
                                dispatch({
                                    type: AUTH_LOGOUT
                                });
                            }
                            dispatch({
                                type: `${typePrefix}_PULLERROR`,
                                error: e
                            });
                        });
                };
            }
        };
    }

    static fromResponse(data) {
        return data;
    }

    static reducer(action, cls) {
        const prefix = `ORM_${cls.modelName.toUpperCase()}`;
        switch (action.type) {
            case `${prefix}_PULLED`:
                const ids = action.payload.map(obj => obj.id);
                cls.exclude(obj => ids.includes(obj.id)).delete();
                action.payload.forEach(obj => cls.upsert(cls.fromResponse(obj)));
                break;
            case `${prefix}_PULLED_DELETEDYNAMIC`:
                cls.delete();
                action.payload.forEach(obj => cls.upsert(cls.fromResponse(obj)));
                break;
            default:
                break;
        }
    }
}

export class Model extends BaseModel {
    static get source() {
        return `/api/db/${this.pluralName}`;
    }

    static get pluralName() {
        return `${this.modelName.toLowerCase()}s`;
    }

    static get isUserData() {
        return true;
    }

    static createAction({ type, payload, effectIfLoggedIn }) {
        return (dispatch, getState) => {
            const state = getState();
            const { auth } = state;
            let action = {
                type: type,
                payload: payload
            };
            if (isRealUser(auth)) {
                action.meta = {
                    offline: {
                        effect: {
                            ...effectIfLoggedIn,
                            body: JSON.stringify(this.toRequest(payload)),
                            headers: {
                                Accept: "application/json",
                                Authorization: "Token " + auth.user.auth_token
                            }
                        },
                        commit: {
                            type: `${type}_PUSHED`
                        },
                        rollback: {
                            type: `${type}_PUSHERROR`,
                            meta: { objectId: payload.id }
                        }
                    }
                };
            }
            dispatch(action);
        };
    }

    static fail(message) {
        message = `LSOHC ORM Error: ${message}`;
        console.error(message);
        throw new Error(message);
    }

    static toRequest(data) {
        var req = {};
        Object.entries(data).forEach(([key, value]) => {
            if (this.fields[key] instanceof ForeignKey) {
                req[key + "_id"] = value;
            } else if (!(key in req)) {
                req[key] = value;
            }
        });
        return req;
    }

    static fromResponse(data) {
        Object.keys(data).forEach(key => {
            const field = key.replace(/_id$/, "");
            if (field !== key && this.fields[field] instanceof ForeignKey) {
                data[field] = data[key];
                delete data[key];
            }
        });
        return data;
    }

    static get actions() {
        const functionPrefix = `orm${this.modelName}`,
            typePrefix = `ORM_${this.modelName.toUpperCase()}`,
            baseActions = super.actions;

        return {
            [`${functionPrefix}Create`]: payload => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState(),
                        { auth } = state;
                    if (!isRealUser(auth)) {
                        dispatch({
                            type: `${typePrefix}_CREATE_PUSHERROR`,
                            error: new Error("Not logged in.")
                        });
                        return;
                    }
                    dispatch({
                        type: `${typePrefix}_CREATE_PUSHING`
                    });
                    return fetch(`${cls.source}?format=json&t=${Date.now()}`, {
                        method: "POST",
                        body: JSON.stringify(cls.toRequest(payload)),
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                            Authorization: "Token " + auth.user.auth_token
                        }
                    })
                        .then(result => {
                            if (!result.ok) {
                                return result.text().then(text => {
                                    throw new Error(text);
                                });
                            } else {
                                return result.json();
                            }
                        })
                        .then(data => {
                            data = cls.fromResponse(data);
                            dispatch({
                                type: `${typePrefix}_CREATE`,
                                payload: data
                            });
                            dispatch({
                                type: `${typePrefix}_CREATE_PUSHED`
                            });
                            return data.id;
                        })
                        .catch(e =>
                            dispatch({
                                type: `${typePrefix}_CREATE_PUSHERROR`,
                                error: e
                            })
                        );
                };
            },
            [`${functionPrefix}LoadDetail`]: (id, callback) => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState();
                    dispatch({
                        type: `${typePrefix}_DETAIL_LOADING`
                    });
                    let options = {};
                    options.headers = {
                        Authorization: "Token " + state.auth.user.auth_token
                    };
                    fetch(`${cls.source}/${id}?format=json`, options)
                        .then(result => result.json())
                        .then(data => {
                            if (data.list) {
                                data = data.list;
                            }
                            if (!(data instanceof Object)) {
                                throw new Error(data.detail || data);
                            }
                            return [data];
                        })
                        .then(data => {
                            dispatch({
                                type: `${typePrefix}_DETAIL_LOADED`,
                                payload: data
                            });
                            if (callback) callback(data);
                        })
                        .catch(e => {
                            dispatch({
                                type: `${typePrefix}_DETAIL_ERROR`,
                                error: e
                            });
                        });
                };
            },
            [`${functionPrefix}LoadDetailChild`]: (id, callback) => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState();
                    dispatch({
                        type: `${typePrefix}_DETAIL_LOADING`
                    });
                    let options = {};
                    options.headers = {
                        Authorization: "Token " + state.auth.user.auth_token
                    };
                    fetch(`${cls.source}?format=json&filter=${id}`, options)
                        .then(result => result.json())
                        .then(data => {
                            if (data.list) {
                                data = data.list;
                            }
                            if (!(data instanceof Array)) {
                                throw new Error(data.detail || data);
                            }
                            return data;
                        })
                        .then(data => {
                            dispatch({
                                type: `${typePrefix}_DETAIL_LOADED`,
                                payload: data
                            });
                            if (callback) callback(data);
                        })
                        .catch(e => {
                            dispatch({
                                type: `${typePrefix}_DETAIL_ERROR`,
                                error: e
                            });
                        });
                };
            },
            [`${functionPrefix}LoadDetailChild2`]: (id, callback) => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState();
                    dispatch({
                        type: `${typePrefix}_DETAIL_LOADING`
                    });
                    let options = {};
                    options.headers = {
                        Authorization: "Token " + state.auth.user.auth_token
                    };
                    fetch(`${cls.source}?format=json&filter2=${id}`, options)
                        .then(result => result.json())
                        .then(data => {
                            if (data.list) {
                                data = data.list;
                            }
                            if (!(data instanceof Array)) {
                                throw new Error(data.detail || data);
                            }
                            return data;
                        })
                        .then(data => {
                            dispatch({
                                type: `${typePrefix}_DETAIL_LOADED`,
                                payload: data
                            });
                            if (callback) callback(data);
                        })
                        .catch(e => {
                            dispatch({
                                type: `${typePrefix}_DETAIL_ERROR`,
                                error: e
                            });
                        });
                };
            },
            [`${functionPrefix}Update2`]: payload => {
                var cls = this;
                return function(dispatch, getState) {
                    const state = getState(),
                        { auth } = state;
                    if (!isRealUser(auth)) {
                        dispatch({
                            type: `${typePrefix}_UPDATE_PUSHERROR`,
                            error: new Error("Not logged in.")
                        });
                        return;
                    }
                    dispatch({
                        type: `${typePrefix}_UPDATE_PUSHING`
                    });
                    return fetch(`${cls.source}/${payload.id}?t=${Date.now()}`, {
                        method: "PATCH",
                        body: JSON.stringify(cls.toRequest(payload)),
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                            Authorization: "Token " + auth.user.auth_token
                        }
                    })
                        .then(result => {
                            if (!result.ok) {
                                return result.text().then(text => {
                                    throw new Error(text);
                                });
                            } else {
                                return result.json();
                            }
                        })
                        .then(data => {
                            data = cls.fromResponse(data);
                            dispatch({
                                type: `${typePrefix}_UPDATE`,
                                payload: data
                            });
                            dispatch({
                                type: `${typePrefix}_UPDATE_PUSHED`
                            });
                            return data;
                        })
                        .catch(e =>
                            dispatch({
                                type: `${typePrefix}_UPDATE_PUSHERROR`,
                                error: e
                            })
                        );
                };
            },
            [`${functionPrefix}Update`]: payload =>
                this.createAction({
                    type: `${typePrefix}_UPDATE`,
                    payload: payload,
                    effectIfLoggedIn: {
                        url: `${this.source}/${payload.id}?t=${Date.now()}`,
                        method: "PATCH"
                    }
                }),
            [`${functionPrefix}UpdateLocalOnly`]: payload => ({
                type: `${typePrefix}_UPDATE`,
                payload: payload
            }),
            [`${functionPrefix}Delete`]: objId =>
                this.createAction({
                    type: `${typePrefix}_DELETE`,
                    payload: { id: objId },
                    effectIfLoggedIn: {
                        url: `${this.source}/${objId}?&t=${Date.now()}`,
                        method: "DELETE"
                    }
                }),
            ...baseActions
        };
    }

    static reducer(action, cls) {
        const prefix = `ORM_${cls.modelName.toUpperCase()}`,
            errorPattern = new RegExp(`^${prefix}_([^_]+)_PUSHERROR$`),
            { payload, meta } = action,
            objId = (payload && payload.id) || (meta && meta.objectId);
        switch (action.type) {
            case `${prefix}_CREATE`:
                cls.create(payload || {});
                break;
            case `${prefix}_UPDATE`:
            case `${prefix}_CREATE_PUSHED`:
            case `${prefix}_UPDATE_PUSHED`:
                if (!cls.hasId(objId)) {
                    break;
                }
                cls.withId(objId).update(action.payload);
                break;
            case `${prefix}_DETAIL_LOADED`:
                action.payload.forEach(obj => cls.upsert(cls.fromResponse(obj)));
                break;
            case `${prefix}_CREATE_PUSHERROR`:
            case `${prefix}_UPDATE_PUSHERROR`:
            case `${prefix}_DETAIL_ERROR`:
                if (!cls.hasId(objId)) {
                    break;
                }
                cls.withId(objId).update({
                    serverError: payload.response || payload.status
                });

                if (prefix === "ORM_INITIALLANDREPORTING" && !timer) {
                    timer = true;
                    alert("There is a problem saving the form.");
                    setTimeout(function() {
                        timer = false;
                    }, 60000);
                }
                break;
            case `${prefix}_DELETE`:
                if (!cls.hasId(objId)) {
                    break;
                }
                cls.withId(objId).delete();
                break;
            default:
                if (action.type.match(errorPattern)) {
                    console.warn(action);
                } else {
                    super.reducer(action, cls);
                }
        }
    }

    _onDelete() {
        const virtualFields = this.getClass().virtualFields;
        for (const key in virtualFields) {
            // eslint-disable-line
            if (this[key] !== null) {
                const relatedQs = this[key];
                if (relatedQs.exists()) {
                    relatedQs.delete();
                }
            }
        }
    }
}

// reloadAll is called in a variety of places - most noticeably if a user first hits the main pages this is called, and then called again when they log in.
// This creates a backload of requests which can take 10-20 or more seconds to clear out.
// To help alleviate this only call every model once during the initial page load in index.js.
// But everywhere else we don't need to reload the ReadOnlyModels (!isUserData) since they never change.
// This should cut down the number of requests to the server.
export function reloadAll() {
    return function(dispatch) {
        orm.registry.forEach(model => {
            if (model.isUserData || model.isSettings) {
                const fn = model.actions[`orm${model.modelName}Reload`];
                dispatch(fn());
            }
        });
    };
}

export function syncReducer(state = {}, action) {
    let { type } = action;
    if (action.meta && action.meta.offline) {
        type = `${type}_PUSHING`;
    }
    const pushPattern = /^ORM_([^_]+)_([^_]+)_(PUSH[^_]+)$/;
    const pullPattern = /^ORM_([^_]+)_((PULL[^_]+))$/;
    const pullPattern2 = /^ORM_([^_]+)_((PULLED_D[^_]+))$/;
    const detailPattern = /^ORM_([^_]+)_(DETAIL)_([^_]+)$/;
    var match = type.match(pushPattern) || type.match(pullPattern) || type.match(detailPattern) || type.match(pullPattern2);
    if (!match) {
        return state;
    }

    let pending = {},
        error = {};
    let [, modelName, actionName, statusName] = match;
    let total = 0;
    orm.registry.forEach(model => {
        if (!model.isUserData) {
            return;
        }
        total += 1;
        if (model.modelName.toUpperCase() === modelName) {
            modelName = model.modelName;
        } else if (state.error && state.error[model.modelName]) {
            error[model.modelName] = state.error[model.modelName];
        } else if (state.pending && state.pending[model.modelName]) {
            pending[model.modelName] = state.pending[model.modelName];
        }
    });

    switch (statusName) {
        case "PUSHING":
        case "PULLING":
        case "LOADING":
            pending[modelName] = actionName;
            break;
        case "PUSHED":
        case "PULLED":
        case "PULLED_DYNAMIC":
        case "LOADED":
            break;
        case "PUSHERROR":
        case "PULLERROR":
        case "ERROR":
            error[modelName] = actionName;
            break;
        default:
            break;
    }
    const pendingCount = Object.keys(pending).length;
    let progress, ready;
    if (pendingCount > 0) {
        progress = total - pendingCount;
        ready = false;
    } else {
        progress = total;
        ready = true;
    }
    return {
        ready,
        progress,
        total,
        pending,
        error
    };
}
